import { getDefaultStore } from 'jotai';
import type { ModelingFormat } from 'balus-common';
import { classNames } from '@framework/utils';
import { FontSize } from '@model-framework/text';
import { DisplayFontSize } from '@model-framework/text/DisplayFontSize';
import { viewModelModelingFormatAtom } from '@user/pages/ViewModelPage/ViewModelModelingFormatAtom';
import type { NodeFontSize } from './domain/NodeStyle/NodeFontSize';
import { StickyNode } from './domain/StickyNode';
import {
  nodeTextareaClass,
  nodeTextareaLang,
  nodeTextareaLineBreakClass,
  nodeTextareaWidth,
} from './components/NodeTextarea/nodeTextareaStyle';

/**
 * 付箋のテキストが付箋内に収まるフォントサイズを返す。
 *
 * 手入力時の自動調整(useFontSizeAdjustTextArea)は編集中のtextareaを計測して動作するため、
 * AIが生成した付箋のように一度も編集されていないものには効かず、文字がはみ出したままになる。
 * そのため生成時にこの関数で同じ基準（実DOMの高さ計測）に揃える。
 *
 * 手入力時と同様に、指定されたサイズより大きくはせず、収まるまで1段階ずつ縮小する。
 */
export function autoFitNodeFontSize(text: string, fontSize: NodeFontSize): NodeFontSize {
  const { modelingFormat } = getDefaultStore().get(viewModelModelingFormatAtom);
  const { width, height } = StickyNode.size();

  return downsizeFontSizeToFit(fontSize, height, (size) => measureNodeTextHeight(text, size, width, modelingFormat));
}

/**
 * テキストの高さが maxHeight に収まるまでフォントサイズを1段階ずつ縮小する。
 * 最小サイズでも収まらない場合は最小サイズを返す。
 */
export function downsizeFontSizeToFit(
  fontSize: NodeFontSize,
  maxHeight: number,
  measureHeight: (fontSize: NodeFontSize) => number
): NodeFontSize {
  let current = fontSize;

  while (measureHeight(current) > maxHeight) {
    const smaller = FontSize.downsizeFont(current);
    if (smaller === current) return current;
    current = smaller;
  }

  return current;
}

type MeasurementElements = {
  container: HTMLDivElement;
  textarea: HTMLTextAreaElement;
};

let measurementElements: MeasurementElements | null = null;

/**
 * 計測用の非表示要素を返す。付箋ごとに要素を作り直すとコストが高いため使い回す。
 * NodeTextarea と同じ構造（付箋幅のコンテナ + テキストエリア）にすることで、
 * 実際の表示と同じ折り返し・高さが得られる。
 */
function getMeasurementElements(): MeasurementElements {
  if (measurementElements && measurementElements.container.isConnected) {
    return measurementElements;
  }

  const container = document.createElement('div');
  container.setAttribute('aria-hidden', 'true');
  container.style.position = 'absolute';
  container.style.top = '0';
  container.style.left = '-99999px';
  container.style.visibility = 'hidden';
  container.style.pointerEvents = 'none';

  const textarea = document.createElement('textarea');
  textarea.rows = 1;
  textarea.readOnly = true;

  container.appendChild(textarea);
  document.body.appendChild(container);

  measurementElements = { container, textarea };
  return measurementElements;
}

function measureNodeTextHeight(
  text: string,
  fontSize: NodeFontSize,
  width: number,
  modelingFormat: ModelingFormat
): number {
  const { container, textarea } = getMeasurementElements();

  container.style.width = `${width}px`;
  container.style.fontSize = DisplayFontSize.getFontSize(fontSize);

  textarea.className = classNames(nodeTextareaClass, nodeTextareaLineBreakClass(modelingFormat));
  const lang = nodeTextareaLang(modelingFormat);
  if (lang) {
    textarea.lang = lang;
  } else {
    textarea.removeAttribute('lang');
  }
  textarea.style.width = nodeTextareaWidth;
  // 高さをautoにすることで、scrollHeightがテキスト全体の高さになる
  textarea.style.height = 'auto';
  textarea.value = text;

  return textarea.scrollHeight;
}
